import { Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  FEATURE_STATES,
  FeatureName,
  FEATURES,
  FeatureState,
  getFeatureDisplayName,
  SELECTABLE_FEATURES,
} from "../../../constants/features";
import { hostname } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";

interface TierFeaturesDialogProps {
  open: boolean;
  onClose: () => void;
  features: { [key: string]: FeatureState };
  onSave: (features: { [key: string]: FeatureState }) => void;
  tierName?: string;
  whitelabelId?: string;
  getToken?: () => Promise<string>;
}

// Base features to show for tier configuration
const BASE_TIER_CONFIGURABLE_FEATURES: FeatureName[] = [
  ...SELECTABLE_FEATURES,
  FEATURES.COLLABORATORS,
  FEATURES.INTEGRATIONS,
];

// Features that require feature flag access
const FEATURE_FLAG_FEATURES: FeatureName[] = [FEATURES.AUDIENCE_BUILDER];

// Function to build feature groups based on available features
const buildFeatureGroups = (
  availableFeatures: FeatureName[]
): Record<string, FeatureName[]> => {
  const groups: Record<string, FeatureName[]> = {
    "Core Features": [
      FEATURES.DASHBOARD,
      FEATURES.CHAT,
      FEATURES.EMPLOYEE,
      FEATURES.KNOWLEDGE_BASE,
    ].filter((f) => availableFeatures.includes(f)),
    "Productivity Tools": [
      FEATURES.WORKFLOW,
      FEATURES.SMART_LIST,
      FEATURES.SEQUENCE,
      FEATURES.INBOX,
      FEATURES.CONTENT_ENGINE,
      FEATURES.AGENT,
    ].filter((f) => availableFeatures.includes(f)),
    "Content Generation": [FEATURES.IMAGE, FEATURES.AUDIO, FEATURES.VIDEO].filter(
      (f) => availableFeatures.includes(f)
    ),
    Administration: [FEATURES.COLLABORATORS, FEATURES.INTEGRATIONS].filter((f) =>
      availableFeatures.includes(f)
    ),
  };

  // Add Audience Builder group for feature flag features
  const audienceBuilderFeatures = [FEATURES.AUDIENCE_BUILDER].filter((f) =>
    availableFeatures.includes(f)
  );
  if (audienceBuilderFeatures.length > 0) {
    groups["Audience Builder"] = audienceBuilderFeatures;
  }

  // Remove empty groups
  return Object.fromEntries(
    Object.entries(groups).filter(([_, features]) => features.length > 0)
  );
};

export default function TierFeaturesDialog({
  open,
  onClose,
  features,
  onSave,
  tierName,
  whitelabelId,
  getToken,
}: TierFeaturesDialogProps) {
  const { employees } = useEmployeeTerminology();
  const [loading, setLoading] = useState(false);
  const [featureStates, setFeatureStates] = useState<{
    [key: string]: FeatureState;
  }>({});
  const [enabledFeatureFlags, setEnabledFeatureFlags] = useState<string[]>([]);

  // Compute configurable features based on base features + enabled feature flags
  const configurableFeatures: FeatureName[] = [
    ...BASE_TIER_CONFIGURABLE_FEATURES,
    ...FEATURE_FLAG_FEATURES.filter((f) => enabledFeatureFlags.includes(f)),
  ];

  // Build feature groups based on available features
  const featureGroups = buildFeatureGroups(configurableFeatures);

  // Fetch enabled feature flags for this whitelabel
  useEffect(() => {
    const fetchEnabledFeatureFlags = async () => {
      if (!getToken || !whitelabelId) {
        setEnabledFeatureFlags([]);
        return;
      }
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/feature-flags/enabled`, {
          params: { whitelabelId },
          headers: { Authorization: `Bearer ${token}` },
        });
        setEnabledFeatureFlags(response.data.features || []);
      } catch (err) {
        console.error("Error fetching enabled feature flags:", err);
        setEnabledFeatureFlags([]);
      }
    };

    if (open) {
      fetchEnabledFeatureFlags();
    }
  }, [open, whitelabelId, getToken]);

  // Initialize feature states when dialog opens
  useEffect(() => {
    if (open) {
      // Initialize with provided features or default to all enabled
      const initialStates: { [key: string]: FeatureState } = {};

      configurableFeatures.forEach((feature) => {
        if (features && features[feature]) {
          initialStates[feature] = features[feature] as FeatureState;
        } else if (FEATURE_FLAG_FEATURES.includes(feature)) {
          // Feature flag features default to hidden if not previously configured
          initialStates[feature] = FEATURE_STATES.HIDDEN;
        } else {
          // Default to enabled for base features
          initialStates[feature] = FEATURE_STATES.ENABLED;
        }
      });

      setFeatureStates(initialStates);
    }
  }, [open, features, configurableFeatures.length]);

  const handleFeatureStateChange = (
    feature: FeatureName,
    state: FeatureState
  ) => {
    setFeatureStates((prev) => ({
      ...prev,
      [feature]: state,
    }));
  };

  const handleSetAllState = (state: FeatureState) => {
    const newStates: { [key: string]: FeatureState } = {};
    configurableFeatures.forEach((feature) => {
      newStates[feature] = state;
    });
    setFeatureStates(newStates);
  };

  const handleSave = () => {
    setLoading(true);
    onSave(featureStates);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          Configure Features{tierName ? ` for ${tierName}` : ""}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Configure which features are available for this subscription tier:
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" component="div">
                • <strong>Enabled:</strong> Feature is fully accessible
              </Typography>
              <Typography variant="body2" component="div">
                • <strong>Locked:</strong> Feature is visible but requires
                upgrade
              </Typography>
              <Typography variant="body2" component="div">
                • <strong>Hidden:</strong> Feature is completely hidden from the
                UI
              </Typography>
            </Box>
          </Alert>

          {/* Quick Actions */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleSetAllState(FEATURE_STATES.ENABLED)}
              >
                Enable All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleSetAllState(FEATURE_STATES.LOCKED)}
              >
                Lock All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleSetAllState(FEATURE_STATES.HIDDEN)}
              >
                Hide All
              </Button>
            </Box>
          </Box>

          {/* Feature Groups */}
          {Object.entries(featureGroups).map(([groupName, groupFeatures]) => (
            <Box key={groupName} sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: "bold" }}
              >
                {groupName}
              </Typography>
              <Grid container spacing={2}>
                {groupFeatures.map((feature) => (
                  <Grid item xs={12} key={feature}>
                    <FormControl component="fieldset" fullWidth>
                      <Box
                        sx={{
                          p: 2,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <FormLabel
                          component="legend"
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "medium",
                            color: "text.primary",
                            mb: 1,
                          }}
                        >
                          {getFeatureDisplayName(feature, employees)}
                        </FormLabel>
                        <RadioGroup
                          row
                          value={
                            featureStates[feature] || FEATURE_STATES.ENABLED
                          }
                          onChange={(e) =>
                            handleFeatureStateChange(
                              feature,
                              e.target.value as FeatureState
                            )
                          }
                        >
                          <Box sx={{ display: "flex", gap: 3 }}>
                            <FormControl>
                              <Radio
                                value={FEATURE_STATES.ENABLED}
                                color="success"
                                size="small"
                              />
                              <Typography variant="caption" align="center">
                                Enabled
                              </Typography>
                            </FormControl>
                            <FormControl>
                              <Radio
                                value={FEATURE_STATES.LOCKED}
                                color="warning"
                                size="small"
                              />
                              <Typography variant="caption" align="center">
                                Locked
                              </Typography>
                            </FormControl>
                            <FormControl>
                              <Radio
                                value={FEATURE_STATES.HIDDEN}
                                color="default"
                                size="small"
                              />
                              <Typography variant="caption" align="center">
                                Hidden
                              </Typography>
                            </FormControl>
                          </Box>
                        </RadioGroup>
                      </Box>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          loading={loading}
        >
          Save Feature Configuration
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
